import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";

export default function SiteFooter() {
  const nav = [
    { label: "Home", to: "/" },
    { label: "Try", to: "/try" },
    { label: "Demo", to: "/demo" },
    { label: "Get in Touch", to: "/contact" },
  ];

  const contact = [
    { k: "Studio", v: "48 Rue de Rivoli, Paris" },
    { k: "Email", v: "hello@topbud.studio" },
    { k: "Phone", v: "+33 1 42 44 08 90" },
    { k: "Hours", v: "Mon — Fri, 10:00 — 18:00" },
  ];

  const socials = [
    { label: "Instagram", tag: "IG", href: "#instagram" },
    { label: "Twitter", tag: "TW", href: "#twitter" },
    { label: "LinkedIn", tag: "IN", href: "#linkedin" },
    { label: "Are.na", tag: "AN", href: "#arena" },
    { label: "Vimeo", tag: "VM", href: "#vimeo" },
  ];

  return (
    <footer className="w-full bg-foreground-950 text-background-50">
      {/* Editorial closing line */}
      <div className="w-full px-6 md:px-10 pt-24 md:pt-32 pb-14 md:pb-20 border-b border-background-50/10">
        <Reveal y={40}>
          <p className="font-label text-[11px] tracking-[0.22em] uppercase text-background-50/60">
            <span>Closing — 06</span>
          </p>
        </Reveal>
        <Reveal delay={140} y={60}>
          <h2
            className="mt-6 font-heading font-semibold leading-[0.92] tracking-[-0.03em] text-background-50 max-w-5xl"
            style={{ fontSize: "clamp(48px, 8vw, 120px)" }}
          >
            Listen more. <br />
            <span className="italic font-normal text-background-50/70">
              Speak less.
            </span>
          </h2>
        </Reveal>
        <Reveal delay={280} y={30}>
          <div className="mt-10 flex items-center gap-4">
            <span className="h-px w-10 bg-background-50/40" />
            <span className="font-label text-[11px] tracking-[0.22em] uppercase text-background-50/60">
              — A companion, not an audience
            </span>
          </div>
        </Reveal>
      </div>

      {/* Info grid */}
      <div className="w-full px-6 md:px-10 py-16 md:py-20 border-b border-background-50/10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          {/* Brand block */}
          <Reveal className="md:col-span-4" y={40}>
            <div className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-background-50 block" />
              <span className="font-heading text-[22px] leading-none tracking-[-0.02em] text-background-50">
                Topbud
              </span>
            </div>
            <p className="mt-8 text-[13px] leading-[1.75] text-background-50/70 max-w-sm text-justify">
              A quietly obsessive design studio building a single AI companion —
              made to sit still, listen, and belong to the room it lives in.
            </p>
            <p className="mt-8 font-label text-[10px] tracking-[0.22em] uppercase text-background-50/50">
              Design Office · Est. 2021
            </p>
          </Reveal>

          {/* Navigate */}
          <Reveal delay={120} className="md:col-span-2" y={40}>
            <p className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/50">
              Navigate
            </p>
            <ul className="mt-6 space-y-3">
              {nav.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    className="cursor-pointer font-label text-[12px] tracking-[0.14em] uppercase text-background-50/80 hover:text-background-50 transition-colors duration-200"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </Reveal>

          {/* Contact */}
          <Reveal delay={200} className="md:col-span-3" y={40}>
            <p className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/50">
              Studio
            </p>
            <ul className="mt-6 space-y-4">
              {contact.map((c) => (
                <li key={c.k}>
                  <p className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/50">
                    {c.k}
                  </p>
                  <p className="mt-1 text-[13px] leading-[1.6] text-background-50/90">
                    {c.v}
                  </p>
                </li>
              ))}
            </ul>
          </Reveal>

          {/* Social */}
          <Reveal delay={280} className="md:col-span-3" y={40}>
            <p className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/50">
              Elsewhere
            </p>
            <ul className="mt-6 space-y-3">
              {socials.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.href}
                    className="cursor-pointer group flex items-center justify-between gap-6 py-1 border-b border-background-50/10 hover:border-background-50/50 transition-colors duration-200"
                  >
                    <span className="font-body text-[13px] text-background-50/90 group-hover:text-background-50 transition-colors">
                      {s.label}
                    </span>
                    <span className="font-label text-[11px] tracking-[0.22em] uppercase text-background-50/50 group-hover:text-background-50 transition-colors">
                      {s.tag} <i className="ri-arrow-right-up-line align-middle" />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>

      {/* Colophon row */}
      <div className="w-full px-6 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/50">
            © 2025 Topbud Studio
          </span>
          <span className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/30 hidden md:inline">
            /
          </span>
          <span className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/50">
            Set in Syne · Manrope · IBM Plex Mono
          </span>
        </div>
        <div className="flex items-center gap-6 font-label text-[10px] tracking-[0.22em] uppercase text-background-50/50">
          <a href="#terms" className="cursor-pointer hover:text-background-50 transition-colors">
            Terms
          </a>
          <a href="#privacy" className="cursor-pointer hover:text-background-50 transition-colors">
            Privacy
          </a>
          <a href="#press" className="cursor-pointer hover:text-background-50 transition-colors">
            Press
          </a>
        </div>
      </div>
    </footer>
  );
}